/**
 *
 * FormB.jsx — Installation Emissions (Sheet B_EmInst)
 *
 * Revision (Jun 2026):
 *   1. AuditSidebar wrapper (matches Form A pattern)
 *   2. PFC tab gated to Aluminium sector only — per CBAM regulation
 *      PFCs apply only to primary aluminium (Hall–Héroult anode effects).
 *      Existing PFC data preserved if user switches away from Aluminium.
 *   3. Default values seeded into row factories AND normalised on Firestore
 *      load (older rows missing default fields get them backfilled).
 *   4. "Other CBAM template columns" panels removed entirely.
 *   5. Colour code reduced to Form A's palette:
 *        - white     = user-fill input cell
 *        - lblue     = auto-filled / auto-calculated (#eff6ff)
 *        - lgrey     = method-disabled (#f1f5f9)
 *      Old yellow/green/lgreen/grey classes are gone.
 *   6. Slim single-line header — "Installation Emissions" only.
 *   7. Section banner restyled to fa-sub-label (small, blue underline).
 *   8. Right panel adds a "Completeness checklist" card for the row being
 *      edited — shows which required fields are filled / missing.
 *   9. Running totals banner restyled with a glossy "jelly" gradient that
 *      still matches the Form A palette (deep blue + cyan accent).
 *  10. NEXT button on tabs a & b, "Save & Continue" on c (navigates to Form C).
 *
 * Revision (Jul 2026):
 *   A. Carbon Content% now marked as required (asterisk + already-existing
 *      amber warning). Not a hard block — matches Form A's soft-required
 *      pattern.
 *   B. Mass Balance: AD split into Input AD + Output AD via two toggle
 *      switches. Input pre-checked, Output off by default. Only shown for
 *      Mass Balance method. Net activityData = Input + Output (Output is
 *      entered as a negative). Single `activityData` still written to
 *      Firestore for backward compatibility with Form C.
 *   C. OxF and Biomass content labels annotated "(Optional)".
 *   D. Linked Process column hidden. Fields (processId, linkedProcessName,
 *      linkedProcessAggregatedGood) remain in state and Firestore writes
 *      so nothing downstream breaks.
 *
 * Behaviour preserved EXACTLY:
 *   - sourceRows / pfcRows / measRows shapes
 *   - newSourceRow / newPFCRow / newMeasurementRow fields
 *   - updSource / updPFC / updMeas all still call BFormulas.recalc*Row
 *   - totals useMemo (Form C reads via engine)
 *   - sectionProgress / calculateFormBProgress / validationSummary
 *   - Firestore onSnapshot + reset-on-auditId
 *   - autoFillEF / autoNcvUnit
 *   - Conditional field gating (Combustion / Mass Balance / Slope / Overvoltage)
 *
 **/
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { firestore as db } from "./firebase.config";
import { doc, setDoc, onSnapshot } from "firebase/firestore";
import { useFormContext } from "../context/FormContext";
import AuditSidebar from "../components/AuditSidebar";
import useAuditProgress from "../hooks/useAuditProgress";
import {
  calculateRequiredProgressForRows,
  FORM_B_REQUIRED_FIELDS,
} from "../utils/CbamProgress";
import { B as BFormulas, CONSTANTS } from "../utils/CbamFormulas";
import { UNIT_GROUPS, UNITS } from "../utils/Cbamunits";
// ─── EF reference data ────────────────────────────────────────────────────────
const COUNTRY_EF_DEFAULTS = {
  IN: { "Natural Gas": null, Coal: null, Oil: null, Biomass: null },
  CN: { "Natural Gas": null, Coal: null, Oil: null, Biomass: null },
  DE: { "Natural Gas": null, Coal: null, Oil: null, Biomass: null },
  RU: { "Natural Gas": null, Coal: null, Oil: null, Biomass: null },
  TR: { "Natural Gas": null, Coal: null, Oil: null, Biomass: null },
  UA: { "Natural Gas": null, Coal: null, Oil: null, Biomass: null },
  GB: { "Natural Gas": null, Coal: null, Oil: null, Biomass: null },
  US: { "Natural Gas": null, Coal: null, Oil: null, Biomass: null },
  ZA: { "Natural Gas": null, Coal: null, Oil: null, Biomass: null },
  BR: { "Natural Gas": null, Coal: null, Oil: null, Biomass: null },
};
const FUEL_TYPES = ["Natural Gas", "Coal", "Oil", "Biomass", "Waste", "Other"];
const EF_SOURCES = ["Measured", "EU default", "Country default"];
const GHG_TYPES = ["CO2", "N2O", "CH4", "PFC", "SF6", "HFC"];
const METHOD_A = ["Combustion", "Mass Balance", "Process Emissions"];
const METHOD_B = ["Slope method", "Overvoltage method"];
// ─── Styles — Form A palette (blue/slate), no more yellow/green coding ────────
const STYLES = `
  .fb { font-family: 'IBM Plex Sans', sans-serif; font-size: 13px; color: #161616; }
  /* Slim header — matches Form A */
  .fb-header-slim {
    background: #fff;
    border-bottom: 2px solid #2563eb;
    padding: 14px clamp(20px, 3vw, 32px);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .fb-header-slim .title {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.2px;
  }
  .fb-header-slim .sub {
    font-size: 11px;
    color: #64748b;
    margin-top: 2px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }
  /* Body wrapper */
  .fb-body {
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    padding: 16px clamp(20px, 3vw, 32px) 24px;
  }
  /* Tabs — match Form A */
  .fb-tabs {
    display: flex;
    gap: 4px;
    border-bottom: 2px solid #2563eb;
    margin-bottom: 18px;
    overflow-x: auto;
  }
  .fb-tab {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    padding: 9px 16px;
    font-size: 13px;
    color: #64748b;
    cursor: pointer;
    border: 1px solid transparent;
    border-bottom: none;
    background: #fff;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    white-space: nowrap;
    user-select: none;
    transition: background 0.15s, color 0.15s;
  }
  .fb-tab:hover:not(.active):not(.disabled) {
    background: #f1f5f9;
    color: #1e293b;
  }
  .fb-tab.active {
    color: #0f172a;
    font-weight: 700;
    border-color: #cbd5e1;
    background: #fff;
  }
  .fb-tab.disabled {
    color: #cbd5e1;
    cursor: not-allowed;
    background: #f8fafc;
  }
  .fb-tab.disabled .fb-tab-num { background: #e2e8f0; color: #cbd5e1; }
  .fb-tab-num {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 11px;
    font-weight: 700;
    background: #e2e8f0;
    color: #475569;
    padding: 2px 7px;
    border-radius: 4px;
    letter-spacing: 0.5px;
  }
  .fb-tab.active .fb-tab-num { background: #2563eb; color: #fff; }
  .fb-tab-badge {
    padding: 2px 8px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 700;
    background: #E0E0E0;
    color: #555;
  }
  .fb-tab-badge.done  { background: #DCFCE7; color: #15803D; }
  .fb-tab-badge.warn  { background: #FEF3C7; color: #D97706; }
  .fb-tab-badge.empty { background: #FEE2E2; color: #DC2626; }
  .fb-tab.disabled .fb-tab-badge {
    background: #f1f5f9 !important;
    color: #cbd5e1 !important;
  }
  /* Tab panel */
  .fb-tab-panel {
    background: #fff;
    border: 1px solid #cbd5e1;
    border-top: none;
    border-radius: 0 0 8px 8px;
    padding: 18px 16px;
    margin-top: -18px;
    margin-bottom: 18px;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  }
  /* Section banner — matches Form A fa-sub-label */
  .fb-section-label {
    font-size: 12px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 12px;
    padding-bottom: 5px;
    border-bottom: 2px solid #2563eb;
    display: inline-block;
  }
  /* Editor & summary panes */
  .fb-editor-card  {
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 14px;
  }
  .fb-summary-card {
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    overflow: hidden;
  }
  .fb-group {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 5px;
    padding: 10px 12px;
    margin-bottom: 10px;
  }
  .fb-group-title {
    font-size: 10px;
    font-weight: 700;
    color: #2563eb;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .fb-field-row {
    display: grid;
    grid-template-columns: 140px 1fr 90px;
    gap: 6px 10px;
    align-items: center;
    margin-bottom: 6px;
  }
  .fb-field-row:last-child { margin-bottom: 0; }
  .fb-lbl { font-size: 11px; color: #475569; font-weight: 600; }
  .fb-lbl.dim { color: #94a3b8; font-weight: 500; }
  .fb-lbl .hint { color: #94a3b8; font-size: 10px; font-style: italic; font-weight: 400; }
  /*  Required-field asterisk — matches Form A's fa-req-star */
  .fb-req-star {
    color: #2563eb;
    font-weight: 700;
    margin-left: 2px;
  }
  /*  Mass Balance AD switch strip */
  .fb-ad-switches {
    display: flex;
    gap: 16px;
    padding: 6px 0 2px;
  }
  .fb-ad-switch {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    user-select: none;
    color: #94a3b8;
    transition: color 0.15s;
  }
  .fb-ad-switch.on { color: #2563eb; }
  .fb-ad-switch input[type="checkbox"] {
    accent-color: #2563eb;
    cursor: pointer;
    margin: 0;
  }
  .fb-ad-switch.disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
  .fb-ad-note {
    font-size: 10px;
    color: #64748b;
    font-style: italic;
    padding: 4px 4px 0;
    line-height: 1.4;
  }
  .fb-cell {
    border: 1px solid #cbd5e1;
    padding: 5px 8px;
    border-radius: 4px;
    font-size: 13px;
    min-height: 28px;
    display: flex;
    align-items: center;
    background: #fff;
  }
  /* 3-state colour scheme */
  .fb-cell.lblue  { background: #eff6ff; border-color: #bfdbfe; }
  .fb-cell.lgrey  { background: #f1f5f9; color: #94a3b8; border-color: #e2e8f0; }
  .fb-cell input, .fb-cell select {
    border: none; outline: none; background: transparent;
    width: 100%; font-family: inherit; font-size: 13px;
    padding: 0; color: #161616;
  }
  .fb-cell.lgrey input, .fb-cell.lgrey select { color: #94a3b8; }
  .fb-cell input:focus, .fb-cell select:focus { outline: none; }
  .fb-cell:focus-within { border-color: #2563eb; }
  .fb-cell.full-width { grid-column: 2 / 4; }
  /* Computed strip — this-row computed values */
  .fb-calc-strip {
    position: relative;
    background: linear-gradient(180deg, #f8fbff 0%, #eff6ff 100%);
    border: 1px solid #dbeafe;
    border-radius: 6px;
    padding: 10px 14px 10px 18px;
    display: flex;
    gap: 28px;
    margin-top: 8px;
    flex-wrap: wrap;
    overflow: hidden;
  }
  .fb-calc-strip::before {
    content: "";
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, #1e40af 0%, #2563eb 50%, #06b6d4 100%);
  }
  .fb-calc-strip .k {
    font-size: 9px;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    font-weight: 700;
    margin-bottom: 3px;
  }
  .fb-calc-strip .v {
    font-family: 'IBM Plex Mono', monospace;
    font-weight: 700;
    font-size: 15px;
    color: #0f172a;
    letter-spacing: -0.2px;
  }
  /* Summary header */
  .fb-summary-hdr {
    background: #f8fafc;
    color: #1e293b;
    padding: 9px 12px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid #e2e8f0;
  }
  /* Summary list item */
  .fb-row-item {
    padding: 9px 12px;
    background: #fff;
    border-bottom: 1px solid #f1f5f9;
    cursor: pointer;
    transition: background 0.12s;
  }
  .fb-row-item:hover { background: #f8fafc; }
  .fb-row-item.editing {
    background: #eff6ff;
    border-left: 3px solid #2563eb;
  }
  .fb-row-item.empty-row { color: #cbd5e1; cursor: default; }
  .fb-row-item .row-line1 {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .fb-row-item .row-line2 {
    font-size: 10px;
    color: #64748b;
    margin-top: 3px;
  }
  .fb-row-item .row-line3 {
    font-size: 11px;
    margin-top: 2px;
    font-family: 'IBM Plex Mono', monospace;
    color: #334155;
  }
  /* Status badges */
  .fb-status {
    display: inline-block;
    font-size: 9px;
    font-weight: 700;
    padding: 1px 5px;
    border-radius: 3px;
    margin-left: 4px;
    vertical-align: middle;
  }
  .fb-status.ok           { background: #DCFCE7; color: #15803D; }
  .fb-status.incomplete   { background: #FEF3C7; color: #D97706; }
  .fb-status.inconsistent { background: #FEE2E2; color: #DC2626; }
  /* Completeness checklist */
  .fb-checklist {
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    padding: 10px 12px;
  }
  .fb-checklist-title {
    font-size: 10px;
    font-weight: 700;
    color: #2563eb;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-bottom: 8px;
  }
  .fb-checklist-row {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    padding: 2px 0;
  }
  .fb-checklist-row.done { color: #15803D; }
  .fb-checklist-row.todo { color: #64748b; }
  /* Section-wide running totals */
  .fb-running-totals {
    position: relative;
    background: linear-gradient(180deg, #f8fbff 0%, #eff6ff 100%);
    border-top: 1px solid #dbeafe;
    padding: 14px 16px 16px 20px;
    margin: 8px 0 0;
    overflow: hidden;
    border-radius: 0 0 6px 6px;
  }
  .fb-running-totals::before {
    content: "";
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, #1e40af 0%, #2563eb 50%, #06b6d4 100%);
  }
  .fb-running-totals .lbl {
    font-size: 9px;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    font-weight: 700;
    margin-bottom: 10px;
  }
  .fb-running-totals .grid-tot {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 18px;
  }
  .fb-running-totals .grid-tot .lbl {
    margin-bottom: 3px;
    font-size: 9px;
  }
  .fb-running-totals .v {
    font-family: 'IBM Plex Mono', monospace;
    font-weight: 700;
    font-size: 14px;
    color: #1e293b;
    letter-spacing: -0.2px;
  }
  .fb-running-totals .grand {
    border-top: 1px solid #cbd5e1;
    margin-top: 12px;
    padding-top: 10px;
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }
  .fb-running-totals .grand-label {
    font-size: 11px;
    color: #475569;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .fb-running-totals .grand-value {
    font-family: 'IBM Plex Mono', monospace;
    font-weight: 700;
    font-size: 20px;
    color: #1e40af;
    letter-spacing: -0.4px;
  }
  /* Footer & buttons */
  .fb-footer-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;
    padding-top: 14px;
    border-top: 1px solid #e2e8f0;
  }
  .fb-footer-meta { font-size: 12px; color: #64748b; }
  .fb-btn-prev {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 9px 18px;
    background: #fff; color: #475569;
    border: 1px solid #cbd5e1;
    font-family: inherit;
    font-size: 13px; font-weight: 600;
    cursor: pointer; border-radius: 4px;
  }
  .fb-btn-prev:hover:not(:disabled) { background: #f1f5f9; }
  .fb-btn-prev:disabled { opacity: 0.4; cursor: not-allowed; }
  .fb-btn-save {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 10px 24px;
    background: #2563eb; color: #fff;
    border: none;
    font-family: inherit;
    font-size: 14px; font-weight: 700;
    cursor: pointer; border-radius: 4px;
    transition: background 0.15s;
  }
  .fb-btn-save:hover { background: #1d4ed8; }
  .fb-btn-save:disabled { background: #cbd5e1; cursor: not-allowed; }
  .fb-toast {
    position: fixed; top: 88px; right: 20px; z-index: 1400;
    background: #22c55e; color: #fff;
    padding: 12px 20px; font-size: 13px; font-weight: 600;
    border-radius: 6px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  }
  .fb-toast.error { background: #dc2626; }
`;
// ─── Defaults ─────────────────────────────────────────────────────────────────
const SOURCE_DEFAULTS = {
  adUnit: UNITS.T,
  ncvUnit: UNITS.GJ_PER_T,
  efUnit: UNITS.TCO2_PER_TJ,
  efSource: "Measured",
  cContentUnit: UNITS.TC_PER_T,
  oxfUnit: UNITS.PERCENT,
  oxidationFactor: "100",
  convfUnit: UNITS.PERCENT,
  convFactor: "100",
  biocUnit: UNITS.PERCENT,
  biomassContent: "0",
  //  Mass balance AD split — Input on by default, Output off
  inputADEnabled: true,
  outputADEnabled: false,
  inputAD: "",
  outputAD: "",
};
const PFC_DEFAULTS = {
  adUnit: UNITS.T,
  gwpCF4: "6630",
  gwpC2F6: "11100",
  collectionEfficiency: "98",
};
const MEAS_DEFAULTS = {
  adUnit: UNITS.T,
  biocUnit: UNITS.PERCENT,
  biomassContent: "0",
  hourlyGHGConcUnit: UNITS.G_PER_NM3,
  hoursOperatingUnit: UNITS.H_PER_PERIOD,
  flueGasFlowUnit: UNITS.KNM3_PER_H,
  annualAmountOfGHGUnit: UNITS.T,
  gwp: "1",
};
// Normalise — backfill any defaults missing on a loaded row
const normalizeSourceRow = (r) => ({
  ...SOURCE_DEFAULTS,
  ...r,
  oxidationFactor: r.oxidationFactor || SOURCE_DEFAULTS.oxidationFactor,
  convFactor: r.convFactor || SOURCE_DEFAULTS.convFactor,
  biomassContent: r.biomassContent || SOURCE_DEFAULTS.biomassContent,
  //  Explicit fallback (?? not ||) so `false` on outputADEnabled is preserved
  inputADEnabled: r.inputADEnabled ?? SOURCE_DEFAULTS.inputADEnabled,
  outputADEnabled: r.outputADEnabled ?? SOURCE_DEFAULTS.outputADEnabled,
});
const normalizePFCRow = (r) => ({
  ...PFC_DEFAULTS,
  ...r,
  gwpCF4: r.gwpCF4 || PFC_DEFAULTS.gwpCF4,
  gwpC2F6: r.gwpC2F6 || PFC_DEFAULTS.gwpC2F6,
  collectionEfficiency:
    r.collectionEfficiency || PFC_DEFAULTS.collectionEfficiency,
});
const normalizeMeasRow = (r) => ({
  ...MEAS_DEFAULTS,
  ...r,
  biomassContent: r.biomassContent || MEAS_DEFAULTS.biomassContent,
  gwp: r.gwp || MEAS_DEFAULTS.gwp,
});
// ─── Row factories ────────────────────────────────────────────────────────────
const uid = () => crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
const newSourceRow = () => ({
  _id: uid(),
  //  processId + linkedProcess fields still initialised so downstream
  //  forms don't hit undefined. UI column for linked process is hidden.
  processId: "",
  linkedProcessName: "",
  linkedProcessAggregatedGood: "",
  method: "",
  sourceName: "",
  fuelType: "",
  activityData: "",
  adUnit: SOURCE_DEFAULTS.adUnit,
  //  Mass balance AD split
  inputADEnabled: SOURCE_DEFAULTS.inputADEnabled,
  outputADEnabled: SOURCE_DEFAULTS.outputADEnabled,
  inputAD: "",
  outputAD: "",
  ncv: "",
  ncvUnit: SOURCE_DEFAULTS.ncvUnit,
  ef: "",
  efUnit: SOURCE_DEFAULTS.efUnit,
  efSource: SOURCE_DEFAULTS.efSource,
  carbonContent: "",
  cContentUnit: SOURCE_DEFAULTS.cContentUnit,
  oxidationFactor: SOURCE_DEFAULTS.oxidationFactor,
  oxfUnit: SOURCE_DEFAULTS.oxfUnit,
  convFactor: SOURCE_DEFAULTS.convFactor,
  convfUnit: SOURCE_DEFAULTS.convfUnit,
  biomassContent: SOURCE_DEFAULTS.biomassContent,
  biocUnit: SOURCE_DEFAULTS.biocUnit,
  co2eFossil: "",
  co2eBio: "",
  energyFossilTJ: "",
  energyBioTJ: "",
  _validationStatus: "empty",
  _validationMessage: "",
});
const newPFCRow = () => ({
  _id: uid(),
  method: "",
  technologyType: "",
  activityData: "",
  adUnit: PFC_DEFAULTS.adUnit,
  frequency: "",
  duration: "",
  sefCF4: "",
  aeo: "",
  ce: "",
  ovc: "",
  fC2F6: "",
  cf4Emissions: "",
  c2f6Emissions: "",
  gwpCF4: PFC_DEFAULTS.gwpCF4,
  gwpC2F6: PFC_DEFAULTS.gwpC2F6,
  cf4Co2e: "",
  c2f6Co2e: "",
  collectionEfficiency: PFC_DEFAULTS.collectionEfficiency,
  co2eFossil: "",
  co2eBio: "",
  energyFossilTJ: "",
  energyBioTJ: "",
});
const newMeasurementRow = () => ({
  _id: uid(),
  name: "",
  ghgType: "",
  activityData: "",
  adUnit: MEAS_DEFAULTS.adUnit,
  biomassContent: MEAS_DEFAULTS.biomassContent,
  biocUnit: MEAS_DEFAULTS.biocUnit,
  hourlyGHGConc: "",
  hourlyGHGConcUnit: MEAS_DEFAULTS.hourlyGHGConcUnit,
  hoursOperating: "",
  hoursOperatingUnit: MEAS_DEFAULTS.hoursOperatingUnit,
  flueGasFlow: "",
  flueGasFlowUnit: MEAS_DEFAULTS.flueGasFlowUnit,
  annualAmountOfGHG: "",
  annualAmountOfGHGUnit: MEAS_DEFAULTS.annualAmountOfGHGUnit,
  gwp: MEAS_DEFAULTS.gwp,
  co2eFossil: "",
  co2eBio: "",
  energyFossilTJ: "",
  energyBioTJ: "",
});
// ─── EF auto-fill logic ───────────────────────────────────────────────────────
const autoFillEF = (row, installationCountry) => {
  if (row.efSource === "EU default") {
    return CONSTANTS.EU_DEFAULT_EF[row.fuelType] ?? "";
  }
  if (row.efSource === "Country default") {
    const countryEFs = COUNTRY_EF_DEFAULTS[installationCountry] || {};
    const val = countryEFs[row.fuelType];
    return val !== null && val !== undefined ? val : "";
  }
  return row.ef;
};
const autoNcvUnit = (adUnit) => BFormulas.nvcUnitSelector(adUnit);
// ─── Shared select component ──────────────────────────────────────────────────
const S = ({ value, onChange, options, disabled, placeholder }) => (
  <select
    value={value ?? ""}
    onChange={onChange ? (e) => onChange(e.target.value) : undefined}
    disabled={disabled}
  >
    <option value="">{placeholder || "—"}</option>
    {(options || []).map((o) => (
      <option key={o.value ?? o} value={o.value ?? o}>
        {o.label ?? o}
      </option>
    ))}
  </select>
);
// ─── Display helpers ──────────────────────────────────────────────────────────
const roundSafe = (x, d = 4) => {
  const n = Number(x);
  if (isNaN(n) || !isFinite(n)) return "";
  return Math.round((n + Number.EPSILON) * Math.pow(10, d)) / Math.pow(10, d);
};
const toNum = (x) => {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
};
const isFilled = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (typeof value === "number") return !Number.isNaN(value);
  if (typeof value === "boolean") return true;
  return false;
};
const fmtNum = (x, dp = 0) => {
  if (x === "" || x === null || x === undefined) return "—";
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-US", { maximumFractionDigits: dp });
};
const fmtCo2e = (x) => {
  if (x === "" || x === null || x === undefined) return "—";
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-US", { maximumFractionDigits: 6 });
};
const fmtRaw = (x) => {
  if (x === "" || x === null || x === undefined) return "—";
  const n = Number(x);
  if (!Number.isFinite(n)) return String(x);
  return n.toLocaleString("en-US", { maximumFractionDigits: 6 });
};
// ─── Completeness checklist ───────────────────────────────────────────────────
const ChecklistRow = ({ label, ok }) => (
  <div className={`fb-checklist-row ${ok ? "done" : "todo"}`}>
    {ok ? (
      <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
    ) : (
      <RadioButtonUncheckedIcon sx={{ fontSize: 14 }} />
    )}
    <span>{label}</span>
  </div>
);
// ─── Component ────────────────────────────────────────────────────────────────
const EMPTY_ARRAY = [];

const FormB = () => {
  const navigate = useNavigate();
  const { auditId } = useParams();
  const { formsData } = useAuditProgress(auditId);
  const { allFormData, updateForm } = useFormContext();
  // ── Form A data ──────────────────────────────────────────────────────────
  const installationCountry = allFormData?.formA?.country || "";
  const installationSector = allFormData?.formA?.sector || "";
  const formAProcesses = allFormData?.formA?.relevantProcesses || EMPTY_ARRAY;
  const formAPrecursors =
    allFormData?.formA?.purchasedPrecursors || EMPTY_ARRAY;
  //  PFC gate — only enabled for Aluminium
  const pfcEnabled = installationSector === "Aluminium";
  //  processOptions kept in case future features need it; currently unused
  //  in UI since the Linked Process column is hidden. Do not remove — the
  //  processId field is still written to Firestore and other forms may
  //  reference it later.
  // eslint-disable-next-line no-unused-vars
  const processOptions = useMemo(
    () =>
      formAProcesses.map((p) => ({
        value: p.id,
        label: `${p.id} — ${p.name || "Unnamed"}`,
      })),
    [formAProcesses],
  );
  const precursorNames = useMemo(
    () => formAPrecursors.map((p) => p.name).filter(Boolean),
    [formAPrecursors],
  );
  // ── State ────────────────────────────────────────────────────────────────
  const [sourceRows, setSourceRows] = useState([newSourceRow()]);
  const [pfcRows, setPfcRows] = useState([newPFCRow()]);
  const [measRows, setMeasRows] = useState([newMeasurementRow()]);
  const [activeTab, setActiveTab] = useState("a");
  const [selectedSourceIdx, setSelectedSourceIdx] = useState(0);
  const [selectedPfcIdx, setSelectedPfcIdx] = useState(0);
  const [selectedMeasIdx, setSelectedMeasIdx] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };
  //  If user is on PFC tab but sector is no longer Aluminium, bounce them
  //  to section (a). PFC data itself stays untouched.
  useEffect(() => {
    if (activeTab === "b" && !pfcEnabled) setActiveTab("a");
  }, [activeTab, pfcEnabled]);
  const sectionProgress = useMemo(
    () => ({
      a: calculateRequiredProgressForRows(
        sourceRows,
        FORM_B_REQUIRED_FIELDS.sourceStreams,
      ),
      b: calculateRequiredProgressForRows(
        pfcRows,
        FORM_B_REQUIRED_FIELDS.pfcEmissions,
      ),
      c: calculateRequiredProgressForRows(
        measRows,
        FORM_B_REQUIRED_FIELDS.measurementBased,
      ),
    }),
    [sourceRows, pfcRows, measRows],
  );
  // ── Auto-save to context ─────────────────────────────────────────────────
  useEffect(() => {
    if (isLoading) return;
    updateForm("formB", { sourceRows, pfcRows, measRows });
  }, [sourceRows, pfcRows, measRows, isLoading, updateForm]);
  // ── Firestore sync ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!auditId) return;
    setSourceRows([newSourceRow()]);
    setPfcRows([newPFCRow()]);
    setMeasRows([newMeasurementRow()]);
    setSelectedSourceIdx(0);
    setSelectedPfcIdx(0);
    setSelectedMeasIdx(0);
    setIsLoading(true);
    const ref = doc(db, "auditReports", auditId, "form_b", "main");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const d = snap.data();
          if (d.sourceRows?.length)
            setSourceRows(d.sourceRows.map(normalizeSourceRow));
          if (d.pfcRows?.length) setPfcRows(d.pfcRows.map(normalizePFCRow));
          if (d.measRows?.length) setMeasRows(d.measRows.map(normalizeMeasRow));
        }
        setIsLoading(false);
      },
      (e) => {
        console.error(e);
        setIsLoading(false);
      },
    );
    return () => unsub();
  }, [auditId]);
  const calculateFormBProgress = () => {
    const sourceStreams = calculateRequiredProgressForRows(
      sourceRows,
      FORM_B_REQUIRED_FIELDS.sourceStreams,
    );
    const pfcEmissions = calculateRequiredProgressForRows(
      pfcRows,
      FORM_B_REQUIRED_FIELDS.pfcEmissions,
    );
    const measurementBased = calculateRequiredProgressForRows(
      measRows,
      FORM_B_REQUIRED_FIELDS.measurementBased,
    );
    const editableTotal =
      sourceStreams.total + pfcEmissions.total + measurementBased.total;
    const editableFilled =
      sourceStreams.filled + pfcEmissions.filled + measurementBased.filled;
    const progress =
      editableTotal === 0
        ? 0
        : Math.round((editableFilled / editableTotal) * 100);
    return {
      editableTotal,
      editableFilled,
      progress,
      status:
        progress >= 100
          ? "Completed"
          : progress > 0
            ? "In Progress"
            : "Not Started",
      sections: {
        sourceStreams: {
          title: "Calculation-based: Source Streams",
          ...sourceStreams,
        },
        pfcEmissions: { title: "PFC emissions", ...pfcEmissions },
        measurementBased: {
          title: "Measurement-based: Emission Sources",
          ...measurementBased,
        },
      },
    };
  };
  // ── Save ─────────────────────────────────────────────────────────────────
  const saveToFirestore = async () => {
    if (!auditId) return;
    const ref = doc(db, "auditReports", auditId, "form_b", "main");
    const progressMeta = calculateFormBProgress();
    await setDoc(
      ref,
      { sourceRows, pfcRows, measRows, progressMeta, updatedAt: new Date() },
      { merge: true },
    );
    updateForm("formB", { sourceRows, pfcRows, measRows, progressMeta });
  };
  const handleNext = async () => {
    setIsSaving(true);
    try {
      await saveToFirestore();
      if (activeTab === "a") setActiveTab(pfcEnabled ? "b" : "c");
      else if (activeTab === "b") setActiveTab("c");
      window.scrollTo({ top: 0, behavior: "smooth" });
      showToast("Saved");
    } catch (e) {
      console.error(e);
      showToast("Save failed", "error");
    } finally {
      setIsSaving(false);
    }
  };
  const handleSaveAndContinue = async () => {
    setIsSaving(true);
    try {
      await saveToFirestore();
      showToast("Form B saved");
      setTimeout(() => navigate(`/form-c/${auditId}`), 500);
    } catch (e) {
      console.error(e);
      showToast("Save failed", "error");
    } finally {
      setIsSaving(false);
    }
  };
  const handlePrevTab = () => {
    if (activeTab === "c") setActiveTab(pfcEnabled ? "b" : "a");
    else if (activeTab === "b") setActiveTab("a");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  // ── Row updaters — all delegate to engine ────────────────────────────────
  const updSource = (i, field, value) => {
    setSourceRows((prev) => {
      const copy = structuredClone(prev);
      copy[i][field] = value;
      const row = copy[i];
      const isMB = (row.method || "").toLowerCase() === "mass balance";

      //  Mass balance AD split: keep activityData as the net (Input + Output).
      //  Output values are entered as negatives by the user, so simple + works.
      //  Runs when method flips to MB, when either switch toggles, or when
      //  either sub-value edits.
      if (
        isMB &&
        [
          "inputAD",
          "outputAD",
          "inputADEnabled",
          "outputADEnabled",
          "method",
        ].includes(field)
      ) {
        const inp = row.inputADEnabled ? toNum(row.inputAD) : 0;
        const out = row.outputADEnabled ? toNum(row.outputAD) : 0;
        row.activityData = String(inp + out);
      }
      //  Method changed AWAY from mass balance → collapse the split back:
      //  keep whatever the net was as the single activityData, reset outputs.
      if (field === "method" && !isMB) {
        //  activityData already carries the last net value; nothing to change.
        //  Reset the output switch so it doesn't linger silently. Keep values
        //  around in case user comes back to MB.
        row.outputADEnabled = false;
      }

      if (field === "efSource" || field === "fuelType") {
        const ef = autoFillEF(copy[i], installationCountry);
        copy[i].ef =
          ef !== ""
            ? String(ef)
            : field === "efSource" && value === "Measured"
              ? ""
              : copy[i].ef;
      }
      if (field === "adUnit") {
        copy[i].ncvUnit = autoNcvUnit(value);
      }
      copy[i] = BFormulas.recalcSourceRow(copy[i]);
      return copy;
    });
  };
  const updPFC = (i, field, value) => {
    setPfcRows((prev) => {
      const copy = structuredClone(prev);
      copy[i][field] = value;
      copy[i] = BFormulas.recalcPFCRow(copy[i]);
      return copy;
    });
  };
  const updMeas = (i, field, value) => {
    setMeasRows((prev) => {
      const copy = structuredClone(prev);
      copy[i][field] = value;
      copy[i] = BFormulas.recalcMeasurementRow(copy[i]);
      return copy;
    });
  };
  // ── Totals (Form C reads via engine) ─────────────────────────────────────
  const totals = useMemo(() => {
    const aFossil = sourceRows.reduce((s, r) => s + toNum(r.co2eFossil), 0);
    const aBio = sourceRows.reduce((s, r) => s + toNum(r.co2eBio), 0);
    const aEnFossil = sourceRows.reduce(
      (s, r) => s + toNum(r.energyFossilTJ),
      0,
    );
    const aEnBio = sourceRows.reduce((s, r) => s + toNum(r.energyBioTJ), 0);
    const bFossil = pfcRows.reduce((s, r) => s + toNum(r.co2eFossil), 0);
    const cFossil = measRows.reduce((s, r) => s + toNum(r.co2eFossil), 0);
    const cBio = measRows.reduce((s, r) => s + toNum(r.co2eBio), 0);
    const cEnFossil = measRows.reduce((s, r) => s + toNum(r.energyFossilTJ), 0);
    const cEnBio = measRows.reduce((s, r) => s + toNum(r.energyBioTJ), 0);
    return {
      aFossil: roundSafe(aFossil),
      aBio: roundSafe(aBio),
      aEnFossil: roundSafe(aEnFossil),
      aEnBio: roundSafe(aEnBio),
      bFossil: roundSafe(bFossil),
      cFossil: roundSafe(cFossil),
      cBio: roundSafe(cBio),
      cEnFossil: roundSafe(cEnFossil),
      cEnBio: roundSafe(cEnBio),
      grand: roundSafe(aFossil + bFossil + cFossil),
    };
  }, [sourceRows, pfcRows, measRows]);
  // eslint-disable-next-line no-unused-vars
  const validationSummary = useMemo(() => {
    const errors = sourceRows.filter(
      (r) => r._validationStatus === "inconsistent",
    ).length;
    const warnings = sourceRows.filter(
      (r) => r._validationStatus === "incomplete",
    ).length;
    return { errors, warnings };
  }, [sourceRows]);
  useEffect(() => {
    if (selectedSourceIdx >= sourceRows.length) {
      setSelectedSourceIdx(Math.max(0, sourceRows.length - 1));
    }
  }, [sourceRows.length, selectedSourceIdx]);
  useEffect(() => {
    if (selectedPfcIdx >= pfcRows.length) {
      setSelectedPfcIdx(Math.max(0, pfcRows.length - 1));
    }
  }, [pfcRows.length, selectedPfcIdx]);
  useEffect(() => {
    if (selectedMeasIdx >= measRows.length) {
      setSelectedMeasIdx(Math.max(0, measRows.length - 1));
    }
  }, [measRows.length, selectedMeasIdx]);
  if (isLoading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  const renderRunningTotals = () => (
    <div className="fb-running-totals">
      <div className="lbl">Running totals — entire Form B (a + b + c)</div>
      <div className="grid-tot">
        <div>
          <div className="lbl" style={{ marginBottom: 2 }}>
            CO₂e fossil (a)
          </div>
          <div className="v">{fmtCo2e(totals.aFossil)}</div>
        </div>
        <div>
          <div className="lbl" style={{ marginBottom: 2 }}>
            CO₂e bio (a)
          </div>
          <div className="v">{fmtCo2e(totals.aBio)}</div>
        </div>
        <div>
          <div className="lbl" style={{ marginBottom: 2 }}>
            CO₂e fossil (b PFC)
          </div>
          <div
            className="v"
            style={{ opacity: toNum(totals.bFossil) === 0 ? 0.5 : 1 }}
          >
            {fmtCo2e(totals.bFossil)}
          </div>
        </div>
        <div>
          <div className="lbl" style={{ marginBottom: 2 }}>
            CO₂e fossil (c meas)
          </div>
          <div
            className="v"
            style={{ opacity: toNum(totals.cFossil) === 0 ? 0.5 : 1 }}
          >
            {fmtCo2e(totals.cFossil)}
          </div>
        </div>
      </div>
      <div className="grand">
        <span className="grand-label">Grand total</span>
        <span className="grand-value">{fmtCo2e(totals.grand)}</span>
      </div>
    </div>
  );
  // ════════════════════════════════════════════════════════════════════════════
  // SECTION (a) — Source Streams
  // ════════════════════════════════════════════════════════════════════════════
  const renderSectionA = () => {
    const row = sourceRows[selectedSourceIdx] || sourceRows[0];
    if (!row) return null;
    const cb = (row.method || "").toLowerCase() === "combustion";
    const mb = (row.method || "").toLowerCase() === "mass balance";
    const efReadOnly =
      row.efSource === "EU default" ||
      (row.efSource === "Country default" && row.ef !== "");
    const filledCount = sourceRows.filter(
      (r) =>
        isFilled(r.method) &&
        isFilled(r.sourceName) &&
        isFilled(r.activityData),
    ).length;
    const onAddRow = () => {
      setSourceRows((p) => {
        const next = [...p, newSourceRow()];
        setSelectedSourceIdx(next.length - 1);
        return next;
      });
    };
    const onDeleteRow = () => {
      if (sourceRows.length === 1) {
        setSourceRows([newSourceRow()]);
        setSelectedSourceIdx(0);
        return;
      }
      setSourceRows((p) => p.filter((_, j) => j !== selectedSourceIdx));
      setSelectedSourceIdx(Math.max(0, selectedSourceIdx - 1));
    };
    const goPrev = () =>
      setSelectedSourceIdx(Math.max(0, selectedSourceIdx - 1));
    const goNext = () =>
      setSelectedSourceIdx(
        Math.min(sourceRows.length - 1, selectedSourceIdx + 1),
      );
    const rowName = row.sourceName || `Row ${selectedSourceIdx + 1}`;
    const checks = [
      { label: "Method selected", ok: isFilled(row.method) },
      { label: "Source name", ok: isFilled(row.sourceName) },
      {
        label: "Activity data + unit",
        ok: isFilled(row.activityData) && isFilled(row.adUnit),
      },
      {
        label: "EF + EF source",
        ok: isFilled(row.ef) && isFilled(row.efSource),
      },
      //  Carbon content is required for all methods (Jul 2026)
      { label: "Carbon content", ok: isFilled(row.carbonContent) },
      ...(cb
        ? [
            { label: "Fuel type (Combustion)", ok: isFilled(row.fuelType) },
            { label: "NCV (Combustion)", ok: isFilled(row.ncv) },
          ]
        : []),
    ];
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "1.7fr 0.7fr",
          },
          gap: 1.5,
        }}
      >
        {/* ───── LEFT: EDITOR PANE ───── */}
        <div className="fb-editor-card">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.2,
              pb: 0.8,
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            <Typography
              sx={{ color: "#1e293b", fontWeight: 700, fontSize: 13 }}
            >
              Editing — Row {selectedSourceIdx + 1} of {sourceRows.length} ·{" "}
              {rowName}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<KeyboardArrowLeftIcon />}
                onClick={goPrev}
                disabled={selectedSourceIdx === 0}
                sx={{ fontSize: 10, py: 0.3, px: 1, minWidth: 0 }}
              >
                Row {Math.max(1, selectedSourceIdx)}
              </Button>
              <Button
                size="small"
                variant="outlined"
                endIcon={<KeyboardArrowRightIcon />}
                onClick={goNext}
                disabled={selectedSourceIdx >= sourceRows.length - 1}
                sx={{ fontSize: 10, py: 0.3, px: 1, minWidth: 0 }}
              >
                Row {Math.min(sourceRows.length, selectedSourceIdx + 2)}
              </Button>
            </Box>
          </Box>
          {row._validationStatus === "incomplete" && row._validationMessage && (
            <Alert severity="warning" sx={{ mb: 1, fontSize: 11, py: 0.3 }}>
              {row._validationMessage}
            </Alert>
          )}
          {row._validationStatus === "inconsistent" &&
            row._validationMessage && (
              <Alert severity="error" sx={{ mb: 1, fontSize: 11, py: 0.3 }}>
                {row._validationMessage}
              </Alert>
            )}
          {/* GROUP 1 — Identity & Source
              NOTE: Linked Process field hidden (Jul 2026). processId is still
              in the row's state and gets written to Firestore — downstream
              forms can continue to read it if they need to. */}
          <div className="fb-group">
            <div className="fb-group-title">▸ Identity &amp; Source</div>
            <div className="fb-field-row">
              <div className="fb-lbl">Method</div>
              <div className="fb-cell full-width">
                <S
                  value={row.method}
                  onChange={(v) => updSource(selectedSourceIdx, "method", v)}
                  options={METHOD_A}
                />
              </div>
            </div>
            <div className="fb-field-row">
              <div className="fb-lbl">Source name</div>
              <div className="fb-cell full-width">
                <input
                  list={`ss-names-${selectedSourceIdx}`}
                  value={row.sourceName}
                  onChange={(e) =>
                    updSource(selectedSourceIdx, "sourceName", e.target.value)
                  }
                />
                <datalist id={`ss-names-${selectedSourceIdx}`}>
                  {precursorNames.map((n) => (
                    <option key={n} value={n} />
                  ))}
                </datalist>
              </div>
            </div>
            <div className="fb-field-row">
              <div className={`fb-lbl ${cb ? "" : "dim"}`}>
                Fuel type{" "}
                {!cb && <span className="hint">(Combustion only)</span>}
              </div>
              <div className={`fb-cell ${cb ? "" : "lgrey"} full-width`}>
                <S
                  value={cb ? row.fuelType : ""}
                  onChange={
                    cb
                      ? (v) => updSource(selectedSourceIdx, "fuelType", v)
                      : undefined
                  }
                  options={FUEL_TYPES}
                  placeholder="Select..."
                  disabled={!cb}
                />
              </div>
            </div>
          </div>
          {/* GROUP 2 — Activity Data & NCV
              For Mass Balance, AD is split into Input AD + Output AD with
              two toggle switches. Input is positive; Output is entered as
              negative. Net activityData = Input + Output flows to the
              engine and Form C unchanged. */}
          <div className="fb-group">
            <div className="fb-group-title">▸ Activity Data &amp; NCV</div>
            {mb ? (
              <>
                {/* Switch strip */}
                <div className="fb-field-row">
                  <div className="fb-lbl">Activity data (AD)</div>
                  <div
                    className="fb-ad-switches"
                    style={{ gridColumn: "2 / 4" }}
                  >
                    <label
                      className={`fb-ad-switch ${row.inputADEnabled ? "on" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={row.inputADEnabled ?? true}
                        onChange={(e) =>
                          updSource(
                            selectedSourceIdx,
                            "inputADEnabled",
                            e.target.checked,
                          )
                        }
                      />
                      Input AD
                    </label>
                    <label
                      className={`fb-ad-switch ${row.outputADEnabled ? "on" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={row.outputADEnabled ?? false}
                        onChange={(e) =>
                          updSource(
                            selectedSourceIdx,
                            "outputADEnabled",
                            e.target.checked,
                          )
                        }
                      />
                      Output AD
                    </label>
                  </div>
                </div>
                {/* Input AD field */}
                {row.inputADEnabled && (
                  <div className="fb-field-row">
                    <div className="fb-lbl">
                      Input AD <span className="hint">(positive)</span>
                    </div>
                    <div className="fb-cell">
                      <input
                        type="number"
                        min="0"
                        value={row.inputAD ?? ""}
                        onChange={(e) =>
                          updSource(
                            selectedSourceIdx,
                            "inputAD",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                    <div className="fb-cell">
                      <S
                        value={row.adUnit}
                        onChange={(v) =>
                          updSource(selectedSourceIdx, "adUnit", v)
                        }
                        options={UNIT_GROUPS.MASS_OR_VOLUME}
                        placeholder="t"
                      />
                    </div>
                  </div>
                )}
                {/* Output AD field */}
                {row.outputADEnabled && (
                  <div className="fb-field-row">
                    <div className="fb-lbl">
                      Output AD <span className="hint">(negative)</span>
                    </div>
                    <div className="fb-cell">
                      <input
                        type="number"
                        max="0"
                        value={row.outputAD ?? ""}
                        onChange={(e) =>
                          updSource(
                            selectedSourceIdx,
                            "outputAD",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                    <div className="fb-cell lblue">{row.adUnit || "t"}</div>
                  </div>
                )}
                {/* Net readout */}
                {(row.inputADEnabled || row.outputADEnabled) && (
                  <div className="fb-field-row">
                    <div className="fb-lbl dim">Net AD (auto)</div>
                    <div className="fb-cell lblue">
                      {fmtNum(row.activityData, 4)}
                    </div>
                    <div className="fb-cell lblue">{row.adUnit || "t"}</div>
                  </div>
                )}
                <div className="fb-ad-note">
                  For mass balance, enter{" "}
                  <strong>Input AD as a positive value</strong> and{" "}
                  <strong>Output AD as a negative value</strong>. Net = Input +
                  Output flows to the calculation.
                </div>
              </>
            ) : (
              /* Non-mass-balance: original single-field layout */
              <div className="fb-field-row">
                <div className="fb-lbl">Activity data (AD)</div>
                <div className="fb-cell">
                  <input
                    type="number"
                    value={row.activityData ?? ""}
                    onChange={(e) =>
                      updSource(
                        selectedSourceIdx,
                        "activityData",
                        e.target.value,
                      )
                    }
                  />
                </div>
                <div className="fb-cell">
                  <S
                    value={row.adUnit}
                    onChange={(v) => updSource(selectedSourceIdx, "adUnit", v)}
                    options={UNIT_GROUPS.MASS_OR_VOLUME}
                    placeholder="t"
                  />
                </div>
              </div>
            )}
            <div className="fb-field-row">
              <div className={`fb-lbl ${cb ? "" : "dim"}`}>
                NCV {!cb && <span className="hint">(Combustion only)</span>}
              </div>
              <div className={`fb-cell ${cb ? "" : "lgrey"}`}>
                <input
                  type="number"
                  value={cb ? (row.ncv ?? "") : ""}
                  onChange={
                    cb
                      ? (e) =>
                          updSource(selectedSourceIdx, "ncv", e.target.value)
                      : undefined
                  }
                  readOnly={!cb}
                />
              </div>
              <div className={`fb-cell ${cb ? "" : "lgrey"}`}>
                <S
                  value={cb ? row.ncvUnit : ""}
                  onChange={
                    cb
                      ? (v) => updSource(selectedSourceIdx, "ncvUnit", v)
                      : undefined
                  }
                  options={UNIT_GROUPS.NCV}
                  disabled={!cb}
                  placeholder="GJ/t"
                />
              </div>
            </div>
          </div>
          {/* GROUP 3 — Emission Factor & Carbon Content */}
          <div className="fb-group">
            <div className="fb-group-title">
              ▸ Emission Factor &amp; Carbon Content
            </div>
            <div className="fb-field-row">
              <div className="fb-lbl">EF</div>
              <div className={`fb-cell ${efReadOnly ? "lblue" : ""}`}>
                <input
                  type="number"
                  value={row.ef ?? ""}
                  onChange={
                    !efReadOnly
                      ? (e) =>
                          updSource(selectedSourceIdx, "ef", e.target.value)
                      : undefined
                  }
                  readOnly={efReadOnly}
                  placeholder={
                    row.efSource === "Country default" && !row.ef
                      ? "verify"
                      : ""
                  }
                />
              </div>
              <div className="fb-cell">
                <S
                  value={row.efUnit}
                  onChange={(v) => updSource(selectedSourceIdx, "efUnit", v)}
                  options={UNIT_GROUPS.EMISSION_FACTOR}
                  placeholder="tCO2/TJ"
                />
              </div>
            </div>
            <div className="fb-field-row">
              <div className="fb-lbl">EF Source</div>
              <div className="fb-cell full-width">
                <S
                  value={row.efSource}
                  onChange={(v) => updSource(selectedSourceIdx, "efSource", v)}
                  options={EF_SOURCES}
                />
              </div>
            </div>
            {/* Carbon Content — required for ALL methods (per senior, Jul 2026).
                Not gated by method anymore; always editable and required. */}
            <div className="fb-field-row">
              <div className="fb-lbl">
                Carbon content<span className="fb-req-star">*</span>
              </div>
              <div className="fb-cell">
                <input
                  type="number"
                  value={row.carbonContent ?? ""}
                  onChange={(e) =>
                    updSource(
                      selectedSourceIdx,
                      "carbonContent",
                      e.target.value,
                    )
                  }
                />
              </div>
              <div className="fb-cell">
                <S
                  value={row.cContentUnit}
                  onChange={(v) =>
                    updSource(selectedSourceIdx, "cContentUnit", v)
                  }
                  options={UNIT_GROUPS.CARBON_CONTENT}
                  placeholder="tC/t"
                />
              </div>
            </div>
            <div
              className="fb-ad-note"
              style={{ color: "#B7791F", fontStyle: "normal", fontWeight: 500 }}
            >
              ⚠ Carbon content is <strong>mandatory</strong> for all methods
              (Combustion, Mass Balance, and Process Emissions).
            </div>
          </div>
          {/* GROUP 4 — Oxidation, Conversion & Biomass */}
          <div className="fb-group">
            <div className="fb-group-title">
              ▸ Oxidation, Conversion &amp; Biomass
            </div>
            {/* OxF — Optional shown in visible black (not muted-italic hint) */}
            <div className="fb-field-row">
              <div className={`fb-lbl ${cb || mb ? "" : "dim"}`}>
                OxF{" "}
                <span style={{ color: "#161616", fontWeight: 600 }}>
                  (Optional)
                </span>
                {!(cb || mb) && (
                  <span className="hint" style={{ marginLeft: 4 }}>
                    · Comb. / MB only
                  </span>
                )}
              </div>
              <div className={`fb-cell ${cb || mb ? "" : "lgrey"}`}>
                <input
                  type="number"
                  value={cb || mb ? (row.oxidationFactor ?? "") : ""}
                  onChange={
                    cb || mb
                      ? (e) =>
                          updSource(
                            selectedSourceIdx,
                            "oxidationFactor",
                            e.target.value,
                          )
                      : undefined
                  }
                  readOnly={!(cb || mb)}
                />
              </div>
              <div className={`fb-cell ${cb || mb ? "lblue" : "lgrey"}`}>
                {cb || mb ? UNITS.PERCENT : ""}
              </div>
            </div>
            <div className="fb-field-row">
              <div className="fb-lbl">
                Conversion factor{" "}
                <span style={{ color: "#161616", fontWeight: 600 }}>
                  (Optional)
                </span>
              </div>
              <div className="fb-cell">
                <input
                  type="number"
                  value={row.convFactor ?? ""}
                  onChange={(e) =>
                    updSource(selectedSourceIdx, "convFactor", e.target.value)
                  }
                />
              </div>
              <div className="fb-cell lblue">{UNITS.PERCENT}</div>
            </div>
            {/* Biomass content — no Optional tag, kept "Combustion only" gate */}
            <div className="fb-field-row">
              <div className={`fb-lbl ${cb ? "" : "dim"}`}>
                Biomass content{" "}
                {!cb && <span className="hint">(Combustion only)</span>}
              </div>
              <div className={`fb-cell ${cb ? "" : "lgrey"}`}>
                <input
                  type="number"
                  value={cb ? (row.biomassContent ?? "") : ""}
                  onChange={
                    cb
                      ? (e) =>
                          updSource(
                            selectedSourceIdx,
                            "biomassContent",
                            e.target.value,
                          )
                      : undefined
                  }
                  readOnly={!cb}
                />
              </div>
              <div className={`fb-cell ${cb ? "lblue" : "lgrey"}`}>
                {cb ? UNITS.PERCENT : ""}
              </div>
            </div>
          </div>
          {/* Computed strip — this row */}
          <div className="fb-calc-strip">
            <div>
              <div className="k">CO₂e FOSSIL</div>
              <div className="v">{fmtCo2e(row.co2eFossil)}</div>
            </div>
            <div>
              <div className="k">CO₂e BIO</div>
              <div
                className="v"
                style={{ opacity: toNum(row.co2eBio) === 0 ? 0.5 : 1 }}
              >
                {fmtCo2e(row.co2eBio)}
              </div>
            </div>
            <div>
              <div className="k">ENERGY F.</div>
              <div
                className="v"
                style={{ opacity: toNum(row.energyFossilTJ) === 0 ? 0.5 : 1 }}
              >
                {row.energyFossilTJ !== "" && row.energyFossilTJ != null
                  ? `${Number(row.energyFossilTJ).toFixed(1)} TJ`
                  : "—"}
              </div>
            </div>
            <div>
              <div className="k">ENERGY BIO</div>
              <div
                className="v"
                style={{ opacity: toNum(row.energyBioTJ) === 0 ? 0.5 : 1 }}
              >
                {row.energyBioTJ !== "" && row.energyBioTJ != null
                  ? `${Number(row.energyBioTJ).toFixed(1)} TJ`
                  : "—"}
              </div>
            </div>
          </div>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mt: 1.2 }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<DeleteOutlineIcon />}
              onClick={onDeleteRow}
              sx={{
                color: "#dc2626",
                borderColor: "#dc2626",
                fontSize: 11,
                py: 0.4,
              }}
            >
              Delete row
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAddRow}
              sx={{
                bgcolor: "#2563eb",
                fontSize: 11,
                py: 0.4,
                "&:hover": { bgcolor: "#1d4ed8" },
              }}
            >
              Add new row
            </Button>
          </Box>
        </div>
        {/* ───── RIGHT: SUMMARY PANE + CHECKLIST ───── */}
        <div className="fb-summary-card">
          <div className="fb-summary-hdr">
            Section (a) — Source Streams · {filledCount} of {sourceRows.length}{" "}
            filled
          </div>
          {sourceRows.map((r, i) => {
            const isEditing = i === selectedSourceIdx;
            const isEmpty =
              !isFilled(r.method) &&
              !isFilled(r.sourceName) &&
              !isFilled(r.activityData);
            const statusBadge =
              r._validationStatus === "ok"
                ? { cls: "ok", label: "OK" }
                : r._validationStatus === "incomplete"
                  ? { cls: "incomplete", label: "!" }
                  : r._validationStatus === "inconsistent"
                    ? { cls: "inconsistent", label: "ERR" }
                    : { cls: "", label: "—" };
            const rIsComb = (r.method || "").toLowerCase() === "combustion";
            return (
              <div
                key={r._id || i}
                className={`fb-row-item ${isEditing ? "editing" : ""} ${isEmpty ? "empty-row" : ""}`}
                onClick={() => !isEmpty && setSelectedSourceIdx(i)}
              >
                <div className="row-line1">
                  <div>
                    <span style={{ color: "#94a3b8", fontSize: 10 }}>
                      #{String(i + 1).padStart(2, "0")}
                    </span>{" "}
                    <b>
                      {r.sourceName || (isEmpty ? "— empty —" : "(unnamed)")}
                    </b>
                    {isEditing && (
                      <span
                        style={{
                          fontSize: 9,
                          color: "#2563eb",
                          fontWeight: 700,
                          marginLeft: 6,
                        }}
                      >
                        · EDITING
                      </span>
                    )}
                  </div>
                  {!isEmpty && statusBadge.label !== "—" && (
                    <span className={`fb-status ${statusBadge.cls}`}>
                      {statusBadge.label}
                    </span>
                  )}
                </div>
                {!isEmpty && (
                  <>
                    <div className="row-line2">
                      {r.method || "(no method)"} · AD {fmtNum(r.activityData)}{" "}
                      {r.adUnit}
                      {rIsComb && r.ncv && ` · NCV ${r.ncv}`}
                      {r.ef && ` · EF ${r.ef} ${r.efUnit}`}
                    </div>
                    <div className="row-line3">
                      CO₂e fossil: {fmtCo2e(r.co2eFossil)}
                      {" · bio: "}
                      {fmtCo2e(r.co2eBio)}
                    </div>
                  </>
                )}
              </div>
            );
          })}
          <div className="fb-checklist">
            <div className="fb-checklist-title">▸ Row completeness</div>
            {checks.map((c, idx) => (
              <ChecklistRow key={idx} {...c} />
            ))}
          </div>
          {renderRunningTotals()}
        </div>
      </Box>
    );
  };
  // ════════════════════════════════════════════════════════════════════════════
  // SECTION (b) — PFC emissions (only when Aluminium)
  // ════════════════════════════════════════════════════════════════════════════
  const renderSectionB = () => {
    if (!pfcEnabled) {
      return (
        <Alert severity="info" sx={{ fontSize: 12 }}>
          PFC emissions apply only to <strong>primary aluminium</strong>{" "}
          production (Hall–Héroult anode effects). The installation sector in
          Form A is
          <strong> {installationSector || "not set"}</strong>, so this section
          is disabled.
        </Alert>
      );
    }
    const row = pfcRows[selectedPfcIdx] || pfcRows[0];
    if (!row) return null;
    const slope = (row.method || "").toLowerCase() === "slope method";
    const ov = (row.method || "").toLowerCase() === "overvoltage method";
    const filledCount = pfcRows.filter(
      (r) =>
        isFilled(r.method) &&
        isFilled(r.technologyType) &&
        isFilled(r.activityData),
    ).length;
    const onAddRow = () => {
      setPfcRows((p) => {
        const next = [...p, newPFCRow()];
        setSelectedPfcIdx(next.length - 1);
        return next;
      });
    };
    const onDeleteRow = () => {
      if (pfcRows.length === 1) {
        setPfcRows([newPFCRow()]);
        setSelectedPfcIdx(0);
        return;
      }
      setPfcRows((p) => p.filter((_, j) => j !== selectedPfcIdx));
      setSelectedPfcIdx(Math.max(0, selectedPfcIdx - 1));
    };
    const goPrev = () => setSelectedPfcIdx(Math.max(0, selectedPfcIdx - 1));
    const goNext = () =>
      setSelectedPfcIdx(Math.min(pfcRows.length - 1, selectedPfcIdx + 1));
    const rowName = row.technologyType || `Row ${selectedPfcIdx + 1}`;
    const checks = [
      { label: "Method selected", ok: isFilled(row.method) },
      { label: "Technology type", ok: isFilled(row.technologyType) },
      {
        label: "Activity data + unit",
        ok: isFilled(row.activityData) && isFilled(row.adUnit),
      },
      ...(slope
        ? [
            { label: "A: Frequency", ok: isFilled(row.frequency) },
            { label: "A: Duration", ok: isFilled(row.duration) },
            { label: "A: SEF (CF4)", ok: isFilled(row.sefCF4) },
          ]
        : []),
      ...(ov
        ? [
            { label: "B: AEO", ok: isFilled(row.aeo) },
            { label: "B: CE", ok: isFilled(row.ce) },
            { label: "B: OVC", ok: isFilled(row.ovc) },
          ]
        : []),
      { label: "F (C2F6)", ok: isFilled(row.fC2F6) },
      {
        label: "Collection efficiency",
        ok: isFilled(row.collectionEfficiency),
      },
    ];
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "1.7fr 0.7fr",
          },
          gap: 1.5,
        }}
      >
        {/* ───── LEFT: EDITOR PANE ───── */}
        <div className="fb-editor-card">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.2,
              pb: 0.8,
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            <Typography
              sx={{ color: "#1e293b", fontWeight: 700, fontSize: 13 }}
            >
              Editing — Row {selectedPfcIdx + 1} of {pfcRows.length} · {rowName}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<KeyboardArrowLeftIcon />}
                onClick={goPrev}
                disabled={selectedPfcIdx === 0}
                sx={{ fontSize: 10, py: 0.3, px: 1, minWidth: 0 }}
              >
                Row {Math.max(1, selectedPfcIdx)}
              </Button>
              <Button
                size="small"
                variant="outlined"
                endIcon={<KeyboardArrowRightIcon />}
                onClick={goNext}
                disabled={selectedPfcIdx >= pfcRows.length - 1}
                sx={{ fontSize: 10, py: 0.3, px: 1, minWidth: 0 }}
              >
                Row {Math.min(pfcRows.length, selectedPfcIdx + 2)}
              </Button>
            </Box>
          </Box>
          <div className="fb-group">
            <div className="fb-group-title">▸ Method &amp; Technology</div>
            <div className="fb-field-row">
              <div className="fb-lbl">Method</div>
              <div className="fb-cell full-width">
                <S
                  value={row.method}
                  onChange={(v) => updPFC(selectedPfcIdx, "method", v)}
                  options={METHOD_B}
                />
              </div>
            </div>
            <div className="fb-field-row">
              <div className="fb-lbl">Technology type</div>
              <div className="fb-cell full-width">
                <input
                  type="text"
                  value={row.technologyType ?? ""}
                  onChange={(e) =>
                    updPFC(selectedPfcIdx, "technologyType", e.target.value)
                  }
                />
              </div>
            </div>
          </div>
          <div className="fb-group">
            <div className="fb-group-title">▸ Activity Data</div>
            <div className="fb-field-row">
              <div className="fb-lbl">Activity data (AD)</div>
              <div className="fb-cell">
                <input
                  type="number"
                  value={row.activityData ?? ""}
                  onChange={(e) =>
                    updPFC(selectedPfcIdx, "activityData", e.target.value)
                  }
                />
              </div>
              <div className="fb-cell">
                <S
                  value={row.adUnit}
                  onChange={(v) => updPFC(selectedPfcIdx, "adUnit", v)}
                  options={UNIT_GROUPS.MASS_OR_VOLUME}
                  placeholder="t"
                />
              </div>
            </div>
          </div>
          <div className="fb-group">
            <div className="fb-group-title">
              ▸ A · Slope method{" "}
              {!slope && (
                <span
                  style={{
                    color: "#94a3b8",
                    fontStyle: "italic",
                    marginLeft: 6,
                    fontWeight: 400,
                  }}
                >
                  (Slope method only)
                </span>
              )}
            </div>
            <div className="fb-field-row">
              <div className={`fb-lbl ${slope ? "" : "dim"}`}>A: Frequency</div>
              <div className={`fb-cell ${slope ? "" : "lgrey"} full-width`}>
                <input
                  type="number"
                  value={slope ? (row.frequency ?? "") : ""}
                  onChange={
                    slope
                      ? (e) =>
                          updPFC(selectedPfcIdx, "frequency", e.target.value)
                      : undefined
                  }
                  readOnly={!slope}
                />
              </div>
            </div>
            <div className="fb-field-row">
              <div className={`fb-lbl ${slope ? "" : "dim"}`}>A: Duration</div>
              <div className={`fb-cell ${slope ? "" : "lgrey"} full-width`}>
                <input
                  type="number"
                  value={slope ? (row.duration ?? "") : ""}
                  onChange={
                    slope
                      ? (e) =>
                          updPFC(selectedPfcIdx, "duration", e.target.value)
                      : undefined
                  }
                  readOnly={!slope}
                />
              </div>
            </div>
            <div className="fb-field-row">
              <div className={`fb-lbl ${slope ? "" : "dim"}`}>A: SEF (CF4)</div>
              <div className={`fb-cell ${slope ? "" : "lgrey"} full-width`}>
                <input
                  type="number"
                  value={slope ? (row.sefCF4 ?? "") : ""}
                  onChange={
                    slope
                      ? (e) => updPFC(selectedPfcIdx, "sefCF4", e.target.value)
                      : undefined
                  }
                  readOnly={!slope}
                />
              </div>
            </div>
          </div>
          <div className="fb-group">
            <div className="fb-group-title">
              ▸ B · Overvoltage method{" "}
              {!ov && (
                <span
                  style={{
                    color: "#94a3b8",
                    fontStyle: "italic",
                    marginLeft: 6,
                    fontWeight: 400,
                  }}
                >
                  (Overvoltage only)
                </span>
              )}
            </div>
            <div className="fb-field-row">
              <div className={`fb-lbl ${ov ? "" : "dim"}`}>B: AEO</div>
              <div className={`fb-cell ${ov ? "" : "lgrey"} full-width`}>
                <input
                  type="number"
                  value={ov ? (row.aeo ?? "") : ""}
                  onChange={
                    ov
                      ? (e) => updPFC(selectedPfcIdx, "aeo", e.target.value)
                      : undefined
                  }
                  readOnly={!ov}
                />
              </div>
            </div>
            <div className="fb-field-row">
              <div className={`fb-lbl ${ov ? "" : "dim"}`}>B: CE</div>
              <div className={`fb-cell ${ov ? "" : "lgrey"} full-width`}>
                <input
                  type="number"
                  value={ov ? (row.ce ?? "") : ""}
                  onChange={
                    ov
                      ? (e) => updPFC(selectedPfcIdx, "ce", e.target.value)
                      : undefined
                  }
                  readOnly={!ov}
                />
              </div>
            </div>
            <div className="fb-field-row">
              <div className={`fb-lbl ${ov ? "" : "dim"}`}>B: OVC</div>
              <div className={`fb-cell ${ov ? "" : "lgrey"} full-width`}>
                <input
                  type="number"
                  value={ov ? (row.ovc ?? "") : ""}
                  onChange={
                    ov
                      ? (e) => updPFC(selectedPfcIdx, "ovc", e.target.value)
                      : undefined
                  }
                  readOnly={!ov}
                />
              </div>
            </div>
          </div>
          <div className="fb-group">
            <div className="fb-group-title">
              ▸ GWP &amp; Collection Efficiency
            </div>
            <div className="fb-field-row">
              <div className="fb-lbl">F (C2F6)</div>
              <div className="fb-cell full-width">
                <input
                  type="number"
                  value={row.fC2F6 ?? ""}
                  onChange={(e) =>
                    updPFC(selectedPfcIdx, "fC2F6", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="fb-field-row">
              <div className="fb-lbl">GWP (CF4)</div>
              <div className="fb-cell full-width">
                <input
                  type="number"
                  value={row.gwpCF4 ?? ""}
                  onChange={(e) =>
                    updPFC(selectedPfcIdx, "gwpCF4", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="fb-field-row">
              <div className="fb-lbl">GWP (C2F6)</div>
              <div className="fb-cell full-width">
                <input
                  type="number"
                  value={row.gwpC2F6 ?? ""}
                  onChange={(e) =>
                    updPFC(selectedPfcIdx, "gwpC2F6", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="fb-field-row">
              <div className="fb-lbl">Collection efficiency</div>
              <div className="fb-cell">
                <input
                  type="number"
                  value={row.collectionEfficiency ?? ""}
                  onChange={(e) =>
                    updPFC(
                      selectedPfcIdx,
                      "collectionEfficiency",
                      e.target.value,
                    )
                  }
                />
              </div>
              <div className="fb-cell lblue">%</div>
            </div>
          </div>
          <div className="fb-group">
            <div className="fb-group-title">▸ Calculated intermediates</div>
            <div className="fb-field-row">
              <div className="fb-lbl">CF4 emissions</div>
              <div className="fb-cell lblue">{fmtRaw(row.cf4Emissions)}</div>
              <div className="fb-cell lblue">t CF4</div>
            </div>
            <div className="fb-field-row">
              <div className="fb-lbl">C2F6 emissions</div>
              <div className="fb-cell lblue">{fmtRaw(row.c2f6Emissions)}</div>
              <div className="fb-cell lblue">t C2F6</div>
            </div>
            <div className="fb-field-row">
              <div className="fb-lbl">CF4 CO₂e</div>
              <div className="fb-cell lblue">{fmtRaw(row.cf4Co2e)}</div>
              <div className="fb-cell lblue">t</div>
            </div>
            <div className="fb-field-row">
              <div className="fb-lbl">C2F6 CO₂e</div>
              <div className="fb-cell lblue">{fmtRaw(row.c2f6Co2e)}</div>
              <div className="fb-cell lblue">t</div>
            </div>
          </div>
          <div className="fb-calc-strip">
            <div>
              <div className="k">CO₂e FOSSIL</div>
              <div className="v">{fmtCo2e(row.co2eFossil)}</div>
            </div>
          </div>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mt: 1.2 }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<DeleteOutlineIcon />}
              onClick={onDeleteRow}
              sx={{
                color: "#dc2626",
                borderColor: "#dc2626",
                fontSize: 11,
                py: 0.4,
              }}
            >
              Delete row
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAddRow}
              sx={{
                bgcolor: "#2563eb",
                fontSize: 11,
                py: 0.4,
                "&:hover": { bgcolor: "#1d4ed8" },
              }}
            >
              Add new row
            </Button>
          </Box>
        </div>
        {/* ───── RIGHT: SUMMARY PANE + CHECKLIST ───── */}
        <div className="fb-summary-card">
          <div className="fb-summary-hdr">
            Section (b) — PFC Emissions · {filledCount} of {pfcRows.length}{" "}
            filled
          </div>
          {pfcRows.map((r, i) => {
            const isEditing = i === selectedPfcIdx;
            const isEmpty =
              !isFilled(r.method) &&
              !isFilled(r.technologyType) &&
              !isFilled(r.activityData);
            return (
              <div
                key={r._id || i}
                className={`fb-row-item ${isEditing ? "editing" : ""} ${isEmpty ? "empty-row" : ""}`}
                onClick={() => !isEmpty && setSelectedPfcIdx(i)}
              >
                <div className="row-line1">
                  <div>
                    <span style={{ color: "#94a3b8", fontSize: 10 }}>
                      #{String(i + 1).padStart(2, "0")}
                    </span>{" "}
                    <b>
                      {r.technologyType ||
                        (isEmpty ? "— empty —" : "(unnamed)")}
                    </b>
                    {isEditing && (
                      <span
                        style={{
                          fontSize: 9,
                          color: "#2563eb",
                          fontWeight: 700,
                          marginLeft: 6,
                        }}
                      >
                        · EDITING
                      </span>
                    )}
                  </div>
                </div>
                {!isEmpty && (
                  <>
                    <div className="row-line2">
                      {r.method || "(no method)"} · AD {fmtNum(r.activityData)}{" "}
                      {r.adUnit}
                      {r.collectionEfficiency &&
                        ` · CE ${r.collectionEfficiency}%`}
                    </div>
                    <div className="row-line3">
                      CF4 {fmtRaw(r.cf4Emissions)} · C2F6{" "}
                      {fmtRaw(r.c2f6Emissions)}
                    </div>
                  </>
                )}
              </div>
            );
          })}
          <div className="fb-checklist">
            <div className="fb-checklist-title">▸ Row completeness</div>
            {checks.map((c, idx) => (
              <ChecklistRow key={idx} {...c} />
            ))}
          </div>
          {renderRunningTotals()}
        </div>
      </Box>
    );
  };
  // ════════════════════════════════════════════════════════════════════════════
  // SECTION (c) — Measurement-based
  // ════════════════════════════════════════════════════════════════════════════
  const renderSectionC = () => {
    const row = measRows[selectedMeasIdx] || measRows[0];
    if (!row) return null;
    const filledCount = measRows.filter(
      (r) =>
        isFilled(r.name) && isFilled(r.ghgType) && isFilled(r.activityData),
    ).length;
    const onAddRow = () => {
      setMeasRows((p) => {
        const next = [...p, newMeasurementRow()];
        setSelectedMeasIdx(next.length - 1);
        return next;
      });
    };
    const onDeleteRow = () => {
      if (measRows.length === 1) {
        setMeasRows([newMeasurementRow()]);
        setSelectedMeasIdx(0);
        return;
      }
      setMeasRows((p) => p.filter((_, j) => j !== selectedMeasIdx));
      setSelectedMeasIdx(Math.max(0, selectedMeasIdx - 1));
    };
    const goPrev = () => setSelectedMeasIdx(Math.max(0, selectedMeasIdx - 1));
    const goNext = () =>
      setSelectedMeasIdx(Math.min(measRows.length - 1, selectedMeasIdx + 1));
    const rowName = row.name || `Row ${selectedMeasIdx + 1}`;
    const checks = [
      { label: "Name", ok: isFilled(row.name) },
      { label: "Type of GHG", ok: isFilled(row.ghgType) },
      {
        label: "Activity data + unit",
        ok: isFilled(row.activityData) && isFilled(row.adUnit),
      },
      { label: "Hourly GHG conc.", ok: isFilled(row.hourlyGHGConc) },
      { label: "Hours operating", ok: isFilled(row.hoursOperating) },
      { label: "Flue gas flow", ok: isFilled(row.flueGasFlow) },
      { label: "GWP", ok: isFilled(row.gwp) },
    ];
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "1.7fr 0.7fr",
          },
          gap: 1.5,
        }}
      >
        <div className="fb-editor-card">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.2,
              pb: 0.8,
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            <Typography
              sx={{ color: "#1e293b", fontWeight: 700, fontSize: 13 }}
            >
              Editing — Row {selectedMeasIdx + 1} of {measRows.length} ·{" "}
              {rowName}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<KeyboardArrowLeftIcon />}
                onClick={goPrev}
                disabled={selectedMeasIdx === 0}
                sx={{ fontSize: 10, py: 0.3, px: 1, minWidth: 0 }}
              >
                Row {Math.max(1, selectedMeasIdx)}
              </Button>
              <Button
                size="small"
                variant="outlined"
                endIcon={<KeyboardArrowRightIcon />}
                onClick={goNext}
                disabled={selectedMeasIdx >= measRows.length - 1}
                sx={{ fontSize: 10, py: 0.3, px: 1, minWidth: 0 }}
              >
                Row {Math.min(measRows.length, selectedMeasIdx + 2)}
              </Button>
            </Box>
          </Box>
          <div className="fb-group">
            <div className="fb-group-title">▸ Identity</div>
            <div className="fb-field-row">
              <div className="fb-lbl">Name</div>
              <div className="fb-cell full-width">
                <input
                  type="text"
                  value={row.name ?? ""}
                  onChange={(e) =>
                    updMeas(selectedMeasIdx, "name", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="fb-field-row">
              <div className="fb-lbl">Type of GHG</div>
              <div className="fb-cell full-width">
                <S
                  value={row.ghgType}
                  onChange={(v) => updMeas(selectedMeasIdx, "ghgType", v)}
                  options={GHG_TYPES}
                  placeholder="Select GHG..."
                />
              </div>
            </div>
          </div>
          <div className="fb-group">
            <div className="fb-group-title">▸ Activity Data &amp; Biomass</div>
            <div className="fb-field-row">
              <div className="fb-lbl">Activity data (AD)</div>
              <div className="fb-cell">
                <input
                  type="number"
                  value={row.activityData ?? ""}
                  onChange={(e) =>
                    updMeas(selectedMeasIdx, "activityData", e.target.value)
                  }
                />
              </div>
              <div className="fb-cell">
                <S
                  value={row.adUnit}
                  onChange={(v) => updMeas(selectedMeasIdx, "adUnit", v)}
                  options={UNIT_GROUPS.MASS_OR_VOLUME}
                  placeholder="t"
                />
              </div>
            </div>
            <div className="fb-field-row">
              <div className="fb-lbl">Biomass content (BioC)</div>
              <div className="fb-cell">
                <input
                  type="number"
                  value={row.biomassContent ?? ""}
                  onChange={(e) =>
                    updMeas(selectedMeasIdx, "biomassContent", e.target.value)
                  }
                />
              </div>
              <div className="fb-cell lblue">{UNITS.PERCENT}</div>
            </div>
          </div>
          <div className="fb-group">
            <div className="fb-group-title">▸ Measurement Chain</div>
            <div className="fb-field-row">
              <div className="fb-lbl">Hourly GHG conc.</div>
              <div className="fb-cell">
                <input
                  type="number"
                  value={row.hourlyGHGConc ?? ""}
                  onChange={(e) =>
                    updMeas(selectedMeasIdx, "hourlyGHGConc", e.target.value)
                  }
                />
              </div>
              <div className="fb-cell lblue">{UNITS.G_PER_NM3}</div>
            </div>
            <div className="fb-field-row">
              <div className="fb-lbl">Hours operating</div>
              <div className="fb-cell">
                <input
                  type="number"
                  value={row.hoursOperating ?? ""}
                  onChange={(e) =>
                    updMeas(selectedMeasIdx, "hoursOperating", e.target.value)
                  }
                />
              </div>
              <div className="fb-cell lblue">{UNITS.H_PER_PERIOD}</div>
            </div>
            <div className="fb-field-row">
              <div className="fb-lbl">Flue gas flow</div>
              <div className="fb-cell">
                <input
                  type="number"
                  value={row.flueGasFlow ?? ""}
                  onChange={(e) =>
                    updMeas(selectedMeasIdx, "flueGasFlow", e.target.value)
                  }
                />
              </div>
              <div className="fb-cell lblue">{UNITS.KNM3_PER_H}</div>
            </div>
          </div>
          <div className="fb-group">
            <div className="fb-group-title">▸ Annual GHG &amp; GWP</div>
            <div className="fb-field-row">
              <div className="fb-lbl">
                Annual GHG <span className="hint">(auto-calc)</span>
              </div>
              <div className="fb-cell lblue">
                {fmtRaw(row.annualAmountOfGHG)}
              </div>
              <div className="fb-cell">
                <S
                  value={row.annualAmountOfGHGUnit}
                  onChange={(v) =>
                    updMeas(selectedMeasIdx, "annualAmountOfGHGUnit", v)
                  }
                  options={UNIT_GROUPS.MASS_OR_VOLUME}
                  placeholder="t"
                />
              </div>
            </div>
            <div className="fb-field-row">
              <div className="fb-lbl">GWP</div>
              <div className="fb-cell full-width">
                <input
                  type="number"
                  value={row.gwp ?? ""}
                  onChange={(e) =>
                    updMeas(selectedMeasIdx, "gwp", e.target.value)
                  }
                />
              </div>
            </div>
          </div>
          <div className="fb-calc-strip">
            <div>
              <div className="k">CO₂e FOSSIL</div>
              <div className="v">{fmtCo2e(row.co2eFossil)}</div>
            </div>
            <div>
              <div className="k">CO₂e BIO</div>
              <div
                className="v"
                style={{ opacity: toNum(row.co2eBio) === 0 ? 0.5 : 1 }}
              >
                {fmtCo2e(row.co2eBio)}
              </div>
            </div>
            <div>
              <div className="k">ENERGY F.</div>
              <div
                className="v"
                style={{ opacity: toNum(row.energyFossilTJ) === 0 ? 0.5 : 1 }}
              >
                {row.energyFossilTJ !== "" && row.energyFossilTJ != null
                  ? `${Number(row.energyFossilTJ).toFixed(1)} TJ`
                  : "—"}
              </div>
            </div>
            <div>
              <div className="k">ENERGY BIO</div>
              <div
                className="v"
                style={{ opacity: toNum(row.energyBioTJ) === 0 ? 0.5 : 1 }}
              >
                {row.energyBioTJ !== "" && row.energyBioTJ != null
                  ? `${Number(row.energyBioTJ).toFixed(1)} TJ`
                  : "—"}
              </div>
            </div>
          </div>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mt: 1.2 }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<DeleteOutlineIcon />}
              onClick={onDeleteRow}
              sx={{
                color: "#dc2626",
                borderColor: "#dc2626",
                fontSize: 11,
                py: 0.4,
              }}
            >
              Delete row
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAddRow}
              sx={{
                bgcolor: "#2563eb",
                fontSize: 11,
                py: 0.4,
                "&:hover": { bgcolor: "#1d4ed8" },
              }}
            >
              Add new row
            </Button>
          </Box>
        </div>
        <div className="fb-summary-card">
          <div className="fb-summary-hdr">
            Section (c) — Measurement-based · {filledCount} of {measRows.length}{" "}
            filled
          </div>
          {measRows.map((r, i) => {
            const isEditing = i === selectedMeasIdx;
            const isEmpty =
              !isFilled(r.name) &&
              !isFilled(r.ghgType) &&
              !isFilled(r.activityData);
            return (
              <div
                key={r._id || i}
                className={`fb-row-item ${isEditing ? "editing" : ""} ${isEmpty ? "empty-row" : ""}`}
                onClick={() => !isEmpty && setSelectedMeasIdx(i)}
              >
                <div className="row-line1">
                  <div>
                    <span style={{ color: "#94a3b8", fontSize: 10 }}>
                      #{String(i + 1).padStart(2, "0")}
                    </span>{" "}
                    <b>{r.name || (isEmpty ? "— empty —" : "(unnamed)")}</b>
                    {isEditing && (
                      <span
                        style={{
                          fontSize: 9,
                          color: "#2563eb",
                          fontWeight: 700,
                          marginLeft: 6,
                        }}
                      >
                        · EDITING
                      </span>
                    )}
                  </div>
                </div>
                {!isEmpty && (
                  <>
                    <div className="row-line2">
                      {r.ghgType || "(no GHG type)"} · AD{" "}
                      {fmtNum(r.activityData)} {r.adUnit}
                      {r.gwp && ` · GWP ${r.gwp}`}
                    </div>
                    <div className="row-line3">
                      Annual GHG: {fmtRaw(r.annualAmountOfGHG)}
                      {" · CO₂e: "}
                      {fmtCo2e(r.co2eFossil)}
                    </div>
                  </>
                )}
              </div>
            );
          })}
          <div className="fb-checklist">
            <div className="fb-checklist-title">▸ Row completeness</div>
            {checks.map((c, idx) => (
              <ChecklistRow key={idx} {...c} />
            ))}
          </div>
          {renderRunningTotals()}
        </div>
      </Box>
    );
  };
  // ─── Main render ───────────────────────────────────────────────────────────
  const tabBadgeClass = (pct) =>
    pct === 100 ? "done" : pct > 0 ? "warn" : "empty";
  const handleTabClick = (tabId) => {
    if (tabId === "b" && !pfcEnabled) return;
    setActiveTab(tabId);
  };
  const pfcTabContent = (
    <div
      className={`fb-tab ${activeTab === "b" ? "active" : ""} ${!pfcEnabled ? "disabled" : ""}`}
      onClick={() => handleTabClick("b")}
    >
      <span className="fb-tab-num">02</span>
      <span>(b) PFC emissions</span>
      <span
        className={`fb-tab-badge ${tabBadgeClass(sectionProgress.b.progress)}`}
      >
        {sectionProgress.b.progress}%
      </span>
    </div>
  );
  const isLastTab = activeTab === "c";
  return (
    <>
      <style>{STYLES}</style>
      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f8fafc" }}>
        <AuditSidebar displayAuditId={auditId} formsData={formsData} />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div className="fb">
            <div className="fb-header-slim">
              <div>
                <div className="title">Installation Emissions</div>
              </div>
            </div>
            <div className="fb-body">
              <div className="fb-section-label">
                Source streams and emission sources
              </div>
              <div className="fb-tabs">
                <div
                  className={`fb-tab ${activeTab === "a" ? "active" : ""}`}
                  onClick={() => handleTabClick("a")}
                >
                  <span className="fb-tab-num">01</span>
                  <span>(a) Source Streams</span>
                  <span
                    className={`fb-tab-badge ${tabBadgeClass(sectionProgress.a.progress)}`}
                  >
                    {sectionProgress.a.progress}%
                  </span>
                </div>
                {pfcEnabled ? (
                  pfcTabContent
                ) : (
                  <Tooltip
                    title="PFC emissions apply only to primary aluminium production. Set sector = Aluminium in Form A to enable."
                    arrow
                    placement="top"
                  >
                    <span>{pfcTabContent}</span>
                  </Tooltip>
                )}
                <div
                  className={`fb-tab ${activeTab === "c" ? "active" : ""}`}
                  onClick={() => handleTabClick("c")}
                >
                  <span className="fb-tab-num">03</span>
                  <span>(c) Measurement-based</span>
                  <span
                    className={`fb-tab-badge ${tabBadgeClass(sectionProgress.c.progress)}`}
                  >
                    {sectionProgress.c.progress}%
                  </span>
                </div>
              </div>
              <div className="fb-tab-panel">
                {activeTab === "a" && renderSectionA()}
                {activeTab === "b" && renderSectionB()}
                {activeTab === "c" && renderSectionC()}
              </div>
              <div className="fb-footer-bar">
                <div className="fb-footer-meta">
                  {activeTab !== "a" && (
                    <button
                      className="fb-btn-prev"
                      onClick={handlePrevTab}
                      disabled={isSaving}
                    >
                      ← Previous tab
                    </button>
                  )}
                </div>
                {isLastTab ? (
                  <button
                    className="fb-btn-save"
                    onClick={handleSaveAndContinue}
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Save & Continue →"}
                  </button>
                ) : (
                  <button
                    className="fb-btn-save"
                    onClick={handleNext}
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Next →"}
                  </button>
                )}
              </div>
            </div>
            {toast && (
              <div
                className={`fb-toast ${toast.type === "error" ? "error" : ""}`}
              >
                {toast.type === "error" ? "×" : "✓"} {toast.msg}
              </div>
            )}
          </div>
        </Box>
      </Box>
    </>
  );
};
export default FormB;
